import React, { useState } from "react";
import { Link, useNavigate } from "react-router";
import { usePost } from "../../api";
import { IKindredDealDetail, KindredPublishStatus } from "../../interfaces";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { ArrowLeft, Plus } from "react-feather";
import { BrandTypeahead, TagListEditor } from "../../components/workbench";

const publishedStatusOptions = [
  { value: KindredPublishStatus.Draft, label: "Draft" },
  { value: KindredPublishStatus.AwaitingApproval, label: "Awaiting Approval" },
  { value: KindredPublishStatus.Approved, label: "Approved" },
  { value: KindredPublishStatus.Deactivated, label: "Deactivated" },
];

export const KindredDealCreate: React.FC = () => {
  const navigate = useNavigate();
  const createDeal = usePost<IKindredDealDetail, any>();

  const [form, setForm] = useState({
    name: "",
    brandId: "",
    brandName: "",
    publishedStatus: KindredPublishStatus.Draft,
    source: "manual",
    description: "",
    shopLink: "",
    startDate: "",
    endDate: "",
    suspended: false,
    featured: false,
    exclusive: false,
    domains: [] as string[],
    countries: [] as string[],
  });
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    form.name.trim() &&
    form.brandId &&
    form.startDate &&
    form.endDate &&
    !createDeal.loading;

  const handleCreate = async () => {
    setError(null);
    try {
      const { brandName, ...data } = form;
      const result = await createDeal.execute("kindred/deals", {
        data: {
          ...data,
          startDate: new Date(data.startDate).toISOString(),
          endDate: new Date(data.endDate).toISOString(),
        },
        comment: comment.trim() || undefined,
      });
      if (result?.id) {
        navigate(`/kindred/deals/${result.id}`);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to create deal");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Create Deal</h4>
        <Link to="/kindred/deals">
          <Button size="sm" variant="outline-secondary">
            <ArrowLeft size={14} className="me-1" /> Back to Deals
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Deal name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <BrandTypeahead
                brandId={form.brandId}
                brandName={form.brandName}
                onChange={(brandId, brandName) =>
                  setForm({ ...form, brandId, brandName })
                }
              />
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="small mb-0">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  size="sm"
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">Shop Link</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.shopLink}
                  onChange={(e) =>
                    setForm({ ...form, shopLink: e.target.value })
                  }
                  placeholder="https://..."
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">Published Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.publishedStatus}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      publishedStatus: Number(e.target.value),
                    })
                  }
                >
                  {publishedStatusOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-0">Source</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Form.Check
                type="switch"
                label="Suspended"
                checked={form.suspended}
                onChange={(e) =>
                  setForm({ ...form, suspended: e.target.checked })
                }
              />
            </Col>
            <Col md="auto">
              <Form.Check
                type="switch"
                label="Featured"
                checked={form.featured}
                onChange={(e) =>
                  setForm({ ...form, featured: e.target.checked })
                }
              />
            </Col>
            <Col md="auto">
              <Form.Check
                type="switch"
                label="Exclusive"
                checked={form.exclusive}
                onChange={(e) =>
                  setForm({ ...form, exclusive: e.target.checked })
                }
              />
            </Col>
          </Row>

          <Row className="mt-3 g-3">
            <Col md={6}>
              <TagListEditor
                label="Domains"
                normalize="lower"
                items={form.domains}
                onChange={(domains) => setForm({ ...form, domains })}
                placeholder="Add domain..."
              />
            </Col>
            <Col md={6}>
              <TagListEditor
                label="Countries"
                items={form.countries}
                onChange={(countries) => setForm({ ...form, countries })}
                placeholder="Add country code..."
              />
            </Col>
          </Row>

          <hr />

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">Comment</Form.Label>
                <Form.Control
                  size="sm"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional reason for creating..."
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button
                variant="primary"
                size="sm"
                disabled={!canSubmit}
                onClick={handleCreate}
              >
                {createDeal.loading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <Plus size={14} className="me-1" />
                )}
                Create Deal
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};
